import { useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import ShareToLinkedRecordsModal from 'components/modals/ShareToLinkedRecordsModal';
import ActivityInput from 'components/shared/activityInput/ActivityInput';
import useActivityInput from 'components/shared/activityInput/useActivityInput';
import Button from 'components/shared/Button';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal, { Actions, ColorThemeName, PatientName } from 'components/shared/StandardModal';
import { useProfile } from 'context/profile';
import { ActivityType } from 'models/Activity';
import LocationEpisode from 'models/LocationEpisode';
import { activityQueryKeys, createActivity } from 'services/api/activity';
import { attachmentsQueryKeys } from 'services/api/attachments';
import { Label } from 'styles/typography';

export type EscalateModalType = {
  setShow: (b: boolean) => void;
  patientName: string;
  locationEpisode: LocationEpisode;
};

export default function EscalateModal(props: EscalateModalType) {
  const { setShow, patientName, locationEpisode } = props;
  const queryClient = useQueryClient();
  const { profile } = useProfile();
  const [isUploading, setIsUploading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const { note, setNote, resetNote, getSerializedNoteValues } = useActivityInput({ escalated: true });
  const noteEmpty = !note.text.trim() && !note.attachments?.length;

  const hasSharedRecordsFlag = profile.hasFlag('shared_records');
  const linkedRecords = locationEpisode.sharedRecords ?? [];
  const hasMentions = note.mentions?.length > 0;
  const shouldShowShareModal = hasSharedRecordsFlag && linkedRecords.length > 0 && !hasMentions;

  const { mutate, isPending } = useMutation({
    mutationFn: createActivity,
  });

  const cancel = () => {
    setShow(false);
    setShowShareModal(false);
    resetNote();
  };

  const postEscalation = (targetLocationEpisodeIds?: string[]) => {
    mutate(
      {
        ...getSerializedNoteValues(),
        locationEpisodeId: locationEpisode.id,
        type: ActivityType.NOTE,
        ...(targetLocationEpisodeIds && { targetLocationEpisodeIds }),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: activityQueryKeys.base });
          queryClient.invalidateQueries({ queryKey: attachmentsQueryKeys.count() });
          cancel();
        },
      }
    );
  };

  const submit = () => {
    if (shouldShowShareModal) {
      setShowShareModal(true);
      return;
    }

    postEscalation();
  };

  if (showShareModal) {
    return (
      <ShareToLinkedRecordsModal
        linkedRecords={linkedRecords}
        currentLocationEpisodeId={locationEpisode.id}
        currentLocationEpisodeName={locationEpisode.owner.client.name}
        patientName={patientName}
        onSubmit={postEscalation}
        onCancel={() => setShowShareModal(false)}
        isSubmitting={isPending}
      />
    );
  }

  return (
    <StandardModal colorTheme={ColorThemeName.Danger} title='Escalation' onCancel={cancel}>
      <PatientName>Patient: {patientName}</PatientName>
      <InputGroup title='Please provide details surrounding the escalation'>
        <ActivityInput
          placeholder={'Enter details surrounding the escalation here'}
          profile={undefined}
          locationEpisodeId={locationEpisode.id}
          values={note}
          setValues={setNote}
          onUploading={setIsUploading}
        />
      </InputGroup>
      <Callout>*Sending an escalation will notify all members of the co-management team</Callout>
      <Actions>
        <Button variant='ghost' onClick={cancel}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={noteEmpty || isUploading} loading={isPending}>
          Confirm
        </Button>
      </Actions>
    </StandardModal>
  );
}

const Callout = styled(Label)`
  background-color: var(--black-05);
  display: flex;
  font-style: italic;
  padding: 10px;
  margin-bottom: 40px;
`;
